import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  CalendarCheck2,
  ChevronDown,
  CreditCard,
  Languages,
  Pin,
  ReceiptText,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { PinDayLogo } from '@/components/ui/PinDayLogo'
import { type Language, type Currency } from './LandingPage'

interface HeroProps {
  language: Language
  currency: Currency
  onLanguageChange: (lang: Language) => void
  onCurrencyChange: (curr: Currency) => void
}

const content = {
  pt: {
    headline: 'Receba o sinal. Bloqueie a agenda.',
    subheadline:
      'Envie proposta, política e pagamento em um link. Quando o cliente paga, PinDay bloqueia a data, horário ou janela configurada.',
    ctaPrimary: 'Criar conta',
    ctaSecondary: 'Ver fluxo',
    proof: 'Menos follow-up manual, menos agenda no talvez, mais clientes com compromisso real.',
    proofPoints: [
      { label: 'Sinal', value: 'pago antes' },
      { label: 'Política', value: 'aceita junto' },
      { label: 'Agenda', value: 'bloqueada' },
    ],
    labels: {
      locale: 'Idioma',
      currency: 'Moeda',
      login: 'Entrar',
      board: 'Reserva de data',
      receipt: 'Sinal confirmado',
      waitlist: 'Fila Pro+',
      hold: 'Data travada',
      policy: 'Cancelamento claro',
      balance: 'Saldo lembrado',
      calendar: 'Calendário',
      week: ['S', 'T', 'Q', 'Q', 'S', 'S', 'D'],
    },
    visual: {
      month: 'JUN',
      day: 'SÁB',
      date: '14',
      client: 'Marina + Theo',
      event: 'Casamento completo',
      receiptNote: 'Sinal recebido',
      slot: 'Conforme o serviço',
      configNote: 'Bloqueio aplicado automaticamente',
      waitlistNote: 'Oferta de 24h se liberar',
      timeline: [
        { label: 'Proposta', detail: 'link enviado' },
        { label: 'Sinal', detail: '30% recebido' },
        { label: 'Calendário', detail: 'dia bloqueado' },
      ],
    },
  },
  en: {
    headline: 'Collect the deposit. Block the calendar.',
    subheadline:
      'Send the proposal, policy, and payment link together. Once the deposit is paid, PinDay blocks the configured date, time slot, or window.',
    ctaPrimary: 'Create account',
    ctaSecondary: 'See flow',
    proof: 'Less manual follow-up, fewer maybe bookings, more clients with real commitment.',
    proofPoints: [
      { label: 'Deposit', value: 'paid first' },
      { label: 'Policy', value: 'accepted too' },
      { label: 'Calendar', value: 'blocked' },
    ],
    labels: {
      locale: 'Language',
      currency: 'Currency',
      login: 'Login',
      board: 'Date reservation',
      receipt: 'Deposit confirmed',
      waitlist: 'Pro+ waitlist',
      hold: 'Date locked',
      policy: 'Clear cancellation',
      balance: 'Balance reminded',
      calendar: 'Calendar',
      week: ['M', 'T', 'W', 'T', 'F', 'S', 'S'],
    },
    visual: {
      month: 'JUN',
      day: 'SAT',
      date: '14',
      client: 'Marina + Theo',
      event: 'Full wedding day',
      receiptNote: 'Payment received',
      slot: 'As configured',
      configNote: 'Block applied automatically',
      waitlistNote: '24h offer if it opens',
      timeline: [
        { label: 'Proposal', detail: 'link sent' },
        { label: 'Deposit', detail: '30% received' },
        { label: 'Calendar', detail: 'day blocked' },
      ],
    },
  },
  es: {
    headline: 'Cobra la seña. Bloquea la agenda.',
    subheadline:
      'Envía propuesta, política y pago en un solo link. Cuando entra la seña, PinDay bloquea la fecha, horario o ventana configurada.',
    ctaPrimary: 'Crear cuenta',
    ctaSecondary: 'Ver flujo',
    proof: 'Menos seguimiento manual, menos agenda en tal vez, más clientes con compromiso real.',
    proofPoints: [
      { label: 'Seña', value: 'pagada antes' },
      { label: 'Política', value: 'aceptada junto' },
      { label: 'Agenda', value: 'bloqueada' },
    ],
    labels: {
      locale: 'Idioma',
      currency: 'Moneda',
      login: 'Entrar',
      board: 'Reserva de fecha',
      receipt: 'Seña confirmada',
      waitlist: 'Lista Pro+',
      hold: 'Fecha bloqueada',
      policy: 'Cancelación clara',
      balance: 'Saldo recordado',
      calendar: 'Calendario',
      week: ['L', 'M', 'X', 'J', 'V', 'S', 'D'],
    },
    visual: {
      month: 'JUN',
      day: 'SÁB',
      date: '14',
      client: 'Marina + Theo',
      event: 'Boda completa',
      receiptNote: 'Seña recibida',
      slot: 'Según el servicio',
      configNote: 'Bloqueo aplicado automáticamente',
      waitlistNote: 'Oferta de 24h si se libera',
      timeline: [
        { label: 'Propuesta', detail: 'link enviado' },
        { label: 'Seña', detail: '30% recibida' },
        { label: 'Calendario', detail: 'día bloqueado' },
      ],
    },
  },
}

const depositByCurrency: Record<Currency, string> = {
  BRL: 'R$ 480',
  USD: '$480',
  EUR: '€480',
}

const calendarCells = ['9', '10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22']

export function Hero({ language, currency, onLanguageChange, onCurrencyChange }: HeroProps) {
  const t = content[language]
  const navigate = useNavigate()
  const [mobilePrefsOpen, setMobilePrefsOpen] = useState(false)
  const localeBase = `/${language}`
  const registerHref = `${localeBase}/register`
  const depositAmount = depositByCurrency[currency]
  const languageOptions: Language[] = ['pt', 'en', 'es']
  const currencyOptions: Currency[] = ['BRL', 'USD', 'EUR']

  return (
    <section className="relative isolate overflow-hidden bg-[#f3eadb] text-[#10283e] [background-image:linear-gradient(rgba(16,40,62,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(16,40,62,0.04)_1px,transparent_1px)] [background-size:48px_48px]">
      <div className="absolute inset-x-0 top-0 h-40 bg-[#fff8ec]/70" />
      <div className="relative mx-auto max-w-7xl px-5 pb-12 pt-5 sm:px-6 lg:pb-16">
        <nav className="relative z-20 border-b border-[#10283e]/14 pb-4">
          <div className="flex items-center justify-between gap-4">
            <PinDayLogo size="md" />
            <div className="flex items-center gap-2 sm:hidden">
              <Link to={`${localeBase}/login`}>
                <Button variant="ghost" className="h-9 px-2.5 text-sm text-[#10283e] hover:bg-[#10283e]/8">
                  {t.labels.login}
                </Button>
              </Link>
              <Link to={registerHref}>
                <Button className="h-9 rounded-md bg-[#10283e] px-3.5 text-sm text-[#fff8ec] hover:bg-[#10283e]/90">
                  {t.ctaPrimary}
                </Button>
              </Link>
            </div>
            <div className="hidden items-center gap-3 sm:flex">
              <div className="flex items-center gap-1 rounded-md border border-[#10283e]/14 bg-[#fff8ec]/72 p-1 text-xs" aria-label={t.labels.locale} role="group">
                <Languages size={14} className="ml-1 text-[#10283e]/45" />
                {languageOptions.map((lang) => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      onLanguageChange(lang)
                      navigate(`/${lang}/`)
                    }}
                    className={cn(
                      'rounded-md px-2.5 py-1 font-medium transition-colors',
                      language === lang ? 'bg-[#10283e] text-[#fff8ec]' : 'text-[#10283e]/64 hover:text-[#10283e]'
                    )}
                  >
                    {lang.toUpperCase()}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1 rounded-md border border-[#10283e]/14 bg-[#fff8ec]/72 p-1 text-xs" aria-label={t.labels.currency} role="group">
                {currencyOptions.map((option) => (
                  <button
                    key={option}
                    type="button"
                    onClick={() => onCurrencyChange(option)}
                    className={cn(
                      'rounded-md px-2.5 py-1 font-medium transition-colors',
                      currency === option ? 'bg-[#e76f28] text-white' : 'text-[#10283e]/64 hover:text-[#10283e]'
                    )}
                  >
                    {option}
                  </button>
                ))}
              </div>

              <Link to={`${localeBase}/login`}>
                <Button variant="ghost" className="text-[#10283e] hover:bg-[#10283e]/8">
                  {t.labels.login}
                </Button>
              </Link>
              <Link to={registerHref}>
                <Button className="bg-[#10283e] text-[#fff8ec] hover:bg-[#10283e]/90">
                  {t.ctaPrimary}
                </Button>
              </Link>
            </div>
          </div>

          <div className="mt-3 sm:hidden">
            <button
              type="button"
              onClick={() => setMobilePrefsOpen((open) => !open)}
              className="flex w-full items-center justify-between rounded-md border border-[#10283e]/16 bg-[#fff8ec]/70 px-4 py-3 text-left text-sm text-[#10283e]"
              aria-expanded={mobilePrefsOpen}
              aria-controls="landing-mobile-prefs"
            >
              <span className="flex items-center gap-2">
                <span className="text-[#10283e]/55">{language.toUpperCase()}</span>
                <span className="text-[#10283e]/28">/</span>
                <span>{currency}</span>
              </span>
              <ChevronDown
                size={16}
                className={cn('transition-transform', mobilePrefsOpen ? 'rotate-180' : 'rotate-0')}
              />
            </button>

            {mobilePrefsOpen ? (
              <div id="landing-mobile-prefs" className="mt-2 grid gap-2 rounded-md border border-[#10283e]/14 bg-[#fff8ec] p-3 shadow-[0_16px_40px_rgba(55,38,18,0.12)]">
                <div className="flex items-center gap-2 rounded-md border border-[#10283e]/12 bg-white/50 px-2 py-1.5 text-[11px]">
                  <Languages size={14} className="text-[#10283e]/48" />
                  <span className="text-[#10283e]/58">{t.labels.locale}</span>
                  {languageOptions.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => {
                        setMobilePrefsOpen(false)
                        onLanguageChange(lang)
                        navigate(`/${lang}/`)
                      }}
                      className={cn(
                        'rounded-md px-2 py-1 font-medium transition-colors',
                        language === lang ? 'bg-[#10283e] text-[#fff8ec]' : 'text-[#10283e]/64 hover:text-[#10283e]'
                      )}
                    >
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>

                <div className="flex items-center gap-2 rounded-md border border-[#10283e]/12 bg-white/50 px-2 py-1.5 text-[11px]">
                  <span className="text-[#10283e]/58">{t.labels.currency}</span>
                  {currencyOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => {
                        setMobilePrefsOpen(false)
                        onCurrencyChange(option)
                      }}
                      className={cn(
                        'rounded-md px-2 py-1 font-medium transition-colors',
                        currency === option ? 'bg-[#e76f28] text-white' : 'text-[#10283e]/64 hover:text-[#10283e]'
                      )}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </nav>

        <div className="relative grid gap-10 pt-10 lg:min-h-[76svh] lg:grid-cols-[minmax(0,0.78fr)_minmax(460px,1fr)] lg:items-center lg:gap-12 lg:pt-12">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: 'easeOut' }}
            className="relative z-10"
          >
            <h1 className="max-w-3xl text-[3.3rem] font-semibold leading-[0.94] tracking-normal text-[#10283e] sm:text-6xl lg:text-[5.1rem]">
              {t.headline}
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-[#10283e]/72 sm:text-xl">
              {t.subheadline}
            </p>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button asChild size="lg" className="h-12 rounded-md bg-[#10283e] px-6 text-[#fff8ec] hover:bg-[#10283e]/90">
                <Link to={registerHref}>
                  {t.ctaPrimary}
                  <ArrowRight size={18} />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="h-12 rounded-md border-[#10283e]/18 bg-[#fff8ec]/70 px-6 text-[#10283e] hover:bg-white">
                <a href="#how-it-works">{t.ctaSecondary}</a>
              </Button>
            </div>

            <p className="mt-8 max-w-2xl border-l-2 border-[#e76f28] pl-4 text-sm leading-6 text-[#10283e]/66 sm:text-base">
              {t.proof}
            </p>

            <dl className="mt-8 grid max-w-2xl grid-cols-3 divide-x divide-[#10283e]/14 border-y border-[#10283e]/14 py-4">
              {t.proofPoints.map((point) => (
                <div key={point.label} className="px-4 first:pl-0 last:pr-0">
                  <dt className="text-xs font-semibold uppercase tracking-[0.18em] text-[#10283e]/42">{point.label}</dt>
                  <dd className="mt-1 text-sm font-semibold text-[#10283e] sm:text-base">{point.value}</dd>
                </div>
              ))}
            </dl>
          </motion.div>

          <motion.div
            aria-hidden="true"
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65, delay: 0.1, ease: 'easeOut' }}
            className="group relative min-h-[390px] sm:min-h-[470px] lg:min-h-[560px]"
          >
            <div className="absolute left-[2%] top-[8%] h-[78%] w-[92%] rotate-[-2deg] border border-[#10283e]/12 bg-[#e5d2b6] shadow-[0_30px_90px_rgba(55,38,18,0.22)]" />
            <motion.div
              whileHover={{ rotate: -1.5, y: -4 }}
              transition={{ type: 'spring', stiffness: 180, damping: 18 }}
              className="absolute left-[4%] top-[10%] w-[72%] max-w-[390px] rotate-[-4deg] border border-[#10283e]/18 bg-[#fff9ee] p-4 shadow-[0_22px_70px_rgba(55,38,18,0.2)] sm:w-[58%] sm:p-6"
            >
              <div className="absolute -top-4 left-1/2 h-8 w-8 -translate-x-1/2 rounded-full border-4 border-[#fff8ec] bg-[#c8462f] shadow-[0_8px_20px_rgba(200,70,47,0.34)]" />
              <div className="flex items-start justify-between gap-4 border-b border-[#10283e]/12 pb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#10283e]/42">{t.labels.calendar}</p>
                  <p className="mt-2 text-lg font-semibold text-[#10283e]">{t.visual.month}</p>
                </div>
                <Pin size={22} className="mt-1 rotate-45 text-[#c8462f]" />
              </div>
              <div className="mt-5 flex items-end justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#e76f28]">{t.visual.day}</p>
                  <p className="font-serif text-[5rem] leading-none tracking-normal text-[#10283e] sm:text-[8rem]">{t.visual.date}</p>
                </div>
                <div className="mb-4 max-w-[92px] text-right sm:max-w-[150px]">
                  <p className="text-xs uppercase tracking-[0.18em] text-[#10283e]/42">{t.visual.client}</p>
                  <p className="mt-2 text-sm font-semibold leading-5 text-[#10283e]">{t.visual.event}</p>
                </div>
              </div>
              <p className="mt-4 inline-flex border border-[#10283e]/12 bg-[#10283e]/5 px-3 py-2 text-xs font-semibold text-[#10283e]/68">
                {t.visual.slot}
              </p>
              <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-semibold uppercase text-[#10283e]/38">
                {t.labels.week.map((day, index) => (
                  <span key={`${day}-${index}`}>{day}</span>
                ))}
              </div>
              <div className="mt-2 grid grid-cols-7 gap-1 text-center text-xs text-[#10283e]/58">
                {calendarCells.map((cell) => (
                  <span
                    key={cell}
                    className={cn(
                      'flex aspect-square items-center justify-center border border-[#10283e]/10 bg-white/45',
                      cell === t.visual.date && 'bg-[#10283e] font-semibold text-[#fff8ec]'
                    )}
                  >
                    {cell}
                  </span>
                ))}
              </div>
            </motion.div>

            <motion.div
              whileHover={{ rotate: 1.5, y: -5 }}
              transition={{ type: 'spring', stiffness: 170, damping: 18 }}
              className="absolute right-[4%] top-[38%] w-[60%] max-w-[350px] rotate-[3deg] border border-[#10283e]/14 bg-[#173247] p-4 text-[#fff8ec] shadow-[0_24px_80px_rgba(16,40,62,0.25)] sm:top-[24%] sm:w-[50%] sm:p-5"
            >
              <div className="flex items-center justify-between border-b border-[#fff8ec]/16 pb-4">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#fff8ec]/50">{t.labels.receipt}</p>
                  <p className="mt-2 text-3xl font-semibold">{depositAmount}</p>
                </div>
                <ReceiptText size={28} className="text-[#f0a34a]" />
              </div>
              <div className="mt-4 grid gap-3 text-sm text-[#fff8ec]/76">
                <div className="flex items-center gap-2">
                  <CreditCard size={16} className="text-[#f0a34a]" />
                  <span>{t.visual.receiptNote}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CalendarCheck2 size={16} className="text-[#f0a34a]" />
                  <span>{t.visual.configNote}</span>
                </div>
              </div>
            </motion.div>

            <div className="absolute bottom-[2%] right-[4%] h-16 w-16 border-b-2 border-r-2 border-[#10283e]/18" />
            <div className="absolute left-[1%] top-[2%] h-14 w-14 border-l-2 border-t-2 border-[#10283e]/18" />
          </motion.div>
        </div>
      </div>
    </section>
  )
}
